import { InputRule } from 'prosemirror-inputrules';
import { Mark } from 'prosemirror-model';
import { EditorState, Plugin, TextSelection } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { getMarkRange } from '../queries/getMarkRange';
import { InputRulesProps, PluginProps } from '../types';
import { sanitizeUrl } from '../utils/url';
import { BaseMark } from './base/BaseMark';

const LINK_INPUT_REGEX = /\[([^[]+)]\((\S+)\)$/;

export class Link extends BaseMark {
  constructor() {
    super('link');
  }

  get schema() {
    return {
      attrs: {
        href: { validate: 'string' },
        title: { default: null, validate: 'string|null' },
        'data-user-id': { default: null, validate: 'string|null' },
      },
      inclusive: false,
      parseDOM: [
        {
          tag: 'a[href]',
          getAttrs(dom: HTMLElement) {
            return {
              href: dom.getAttribute('href'),
              title: dom.getAttribute('title'),
              'data-user-id': dom.getAttribute('data-user-id'),
            };
          },
        },
      ],
      toDOM(node: Mark) {
        const { href, title, 'data-user-id': userId } = node.attrs;
        return ['a', { href, title, 'data-user-id': userId }, 0] as const;
      },
    };
  }

  inputRules({ schema }: InputRulesProps): InputRule[] {
    return [
      new InputRule(LINK_INPUT_REGEX, (state, match, start, end) => {
        const [okay, alt, href] = match;
        const { tr } = state;

        if (okay) {
          tr.replaceWith(start, end, schema.text(alt)).addMark(
            start,
            start + alt.length,
            schema.marks.link.create({ href })
          );
        }

        return tr;
      }),
    ];
  }

  plugins({ schema }: PluginProps): Plugin[] {
    const handleClick = (view: EditorView, pos: number) => {
      const { doc, tr } = view.state;
      const range = getMarkRange(doc.resolve(pos), schema.marks.link);

      if (!range || range.from === pos || range.to === pos) {
        return false;
      }

      try {
        const $start = doc.resolve(range.from);
        const $end = doc.resolve(range.to);
        tr.setSelection(new TextSelection($start, $end));

        view.dispatch(tr);
        return true;
      } catch {
        // Failed to set selection
      }
      return false;
    };

    const plugin: Plugin = new Plugin({
      props: {
        decorations: (state: EditorState) => plugin.getState(state),
        handleDOMEvents: {
          contextmenu: (view: EditorView, event: MouseEvent) => {
            const result = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            });
            if (result) {
              return handleClick(view, result.pos);
            }

            return false;
          },
          mousedown: (view: EditorView, event: MouseEvent) => {
            const target = (event.target as HTMLElement)?.closest('a');
            if (
              !(target instanceof HTMLAnchorElement) ||
              (event.button !== 0 && event.button !== 1)
            ) {
              return false;
            }

            if (target.role === 'button') {
              return false;
            }

            const href =
              target.href ||
              (target.parentNode instanceof HTMLAnchorElement ? target.parentNode.href : '');

            try {
              event.stopPropagation();
              event.preventDefault();
              // this.options.onClickLink(sanitizeUrl(href), event);
              window.open(sanitizeUrl(href), '_blank');
            } catch {
              console.error('Error opening link', href);
            }

            return true;
          },
          click: (_view: EditorView, event: MouseEvent) => {
            if (!(event.target instanceof HTMLAnchorElement) || event.button !== 0) {
              return false;
            }

            if (event.target.role === 'button' || event.target.matches('.component-attachment *')) {
              return false;
            }

            // Prevent all default click behavior of links, see mousedown above
            // for custom link handling.
            event.stopPropagation();
            event.preventDefault();

            return false;
          },
        },
      },
    });

    return [plugin];
  }
}
